import { useSelector } from 'react-redux';
import {
  selectBtCapabilities,
  type BtCapabilitiesState,
  type PaymentMethodIdentity,
} from '@/selectors/basisTheorySelectors';

type SaveCardReduxState = BtCapabilitiesState & {
  purchase: { get: (key: string) => unknown };
  reserhubAccount?: { get: (key: string) => unknown };
};

/**
 * Select whether the current authenticated user can save a Basis Theory card.
 *
 * @param {object} state - Redux state.
 * @returns {boolean} Whether authenticated card saving is available.
 */
function selectCanSaveAuthenticatedCard(state: SaveCardReduxState): boolean {
  const checkoutEnabled = selectStoredCardsCapabilityEnabled(state);
  const user = state.reserhubAccount?.get('user');

  return checkoutEnabled && Boolean(user);
}

/** Select whether the active payment method exposes the stored-card capability. */
function selectStoredCardsCapabilityEnabled(state: SaveCardReduxState): boolean {
  const paymentMethod = state.purchase.get('selectedPaymentMethod') as
    | PaymentMethodIdentity
    | undefined;
  const { checkoutEnabled } = selectBtCapabilities(state, paymentMethod);

  return checkoutEnabled;
}

/** Return whether the current authenticated user can save a Basis Theory card. */
function useCanSaveAuthenticatedCard(): boolean {
  return useSelector<SaveCardReduxState, boolean>(selectCanSaveAuthenticatedCard);
}

/** Return whether stored cards are configured before account ownership is known. */
function useStoredCardsCapabilityEnabled(): boolean {
  return useSelector<SaveCardReduxState, boolean>(selectStoredCardsCapabilityEnabled);
}

export { useCanSaveAuthenticatedCard, useStoredCardsCapabilityEnabled };
